import {
  fetchVehicleSwitches,
  SUCCESS_DASH_CONFIG,
  SUCCESS_SAVE_DASH_CONFIG,
  FAILURE_SAVE_DASH_CONFIG,
  SUCCESS_LAYOUT_BLACKLIST,
  SAVE_DASH,
  setDashBlockedList,
  saveDashConfig,
  displayAlliumDataBanner,
  displayPartsWithoutLocation,
  // displaySavedWithAlliumErrorsBanner,
  removePartsWithoutLocationBanner,
  removeSngPendingNotification,
  saveCustomSwitchConfig,
  resetSwitchDetailsChanged,
  SAVE_CUSTOM_SWITCH_GRAPHICS,
  saveLayoutLocation,
  saveCustomSwitchGraphics,
} from 'actions/dash'
import {
  SET_SERIAL_NUMBER_SELECT,
  updateProdScheduleFromApiResponse,
} from 'actions/dashBuilder'
import { requestVehicleFeatures } from 'actions/truck'

import { setTotalProductionScheduleRecords } from 'actions/search'
import { take, put, select, all, fork, call } from 'redux-saga/effects'
import {
  getAlliumModelId,
  getDashComponentsList,
  getDashIsPreProduction,
  getDashUnassignedList,
  getDashVehicleSerialNumber,
  getIsAllium,
  getRemovedComponentsList,
  getRemovedCustomGraphics,
  getSelectedCustomGraphics,
  getUnassignedComponents,
  getUpdatedLocations,
} from 'selectors/dash'
import {
  TRUCK_SUBTYPE_43N,
  TRUCK_SUBTYPE_42N,
  TRUCK_SUBTYPE_38N,
  TRUCK_SUBTYPE_BUS,
} from 'utils/constants'
import { prop, values, length, pathOr } from 'ramda'
import { createBlockedList } from 'components/SwitchAndGauge/utils'
import * as dashActions from 'actions/dashBuilder'
import {
  getDashboardSlice,
  getProductionScheduleRequestId,
} from 'selectors/dashBuilder'
import { isDashTypeBus } from 'utils/dash'
import {
  createSaveComponentsList,
  buildReplacedSwitchesList,
} from 'sagas/dashSaveUtils'
import { postReplacedSwitches } from 'services/dash'
import { createSystemNotification } from 'actions/application'
import strings from 'utils/strings'
import { SNG_SAVE_SUCCESS } from 'utils/notifications'

const truckThatCanAddSmartSwitches = [
  TRUCK_SUBTYPE_43N,
  TRUCK_SUBTYPE_42N,
  TRUCK_SUBTYPE_38N,
]

export function* watchPaginatedProductionScheduleReceived() {
  while (true) {
    const props = yield take(
      dashActions.PAGINATED_PRODUCTION_SCHEDULE_FROM_API_RECEIVED
    )
    const { requestBody, pageData, data } = props
    const { result, total } = data
    const { date, location, customLayoutSearch, startIndex } = requestBody

    const paginatedSchedule = {
      pageData,
      vehicles: result,
      total,
      date,
      location,
      customLayoutSearch,
      startIndex,
    }
    yield put(dashActions.updatePaginatedProductionSchedule(paginatedSchedule))
  }
}

/**
 * Single guarded terminal path for every production-schedule response, success
 * and failure alike (Bug 508529).
 *
 * The whole production-schedule query — plant, date, pagination index, search
 * string, daysOut, custom-layout flag, filter terms — can change while a request
 * is in flight: pagination writes the new startIndex before the request is
 * issued, and the searchbar stays interactive throughout. So a slow response can
 * land after the user has moved on, and applying it unconditionally either
 * repopulates the table with another query's rows or blanks the page now on
 * screen with an error for a page that loaded fine.
 *
 * Rather than compare each query field — where anything forgotten becomes a
 * silent race — every request is stamped with an incrementing requestId that
 * comes back on the response. Only the newest request may touch the store.
 *
 * Responses with no requestId are applied rather than dropped, so an unstamped
 * caller can never leave the table permanently stale.
 */
export function* watchProductionScheduleResult() {
  while (true) {
    const action = yield take([
      dashActions.PRODUCTION_SCHEDULE_RESPONSE_RECEIVED,
      dashActions.PRODUCTION_SCHEDULE_REQUEST_FAILED,
    ])

    const currentRequestId = yield select(getProductionScheduleRequestId)
    const { requestId } = action
    const isCurrent = requestId == null || requestId === currentRequestId

    if (!isCurrent) continue // eslint-disable-line no-continue

    if (action.type === dashActions.PRODUCTION_SCHEDULE_REQUEST_FAILED) {
      yield put(dashActions.productionSchedulePageFailed(requestId))
    } else {
      const { data, collectionName } = action
      const { result, total } = data
      yield put(setTotalProductionScheduleRecords(total, collectionName))
      yield put(updateProdScheduleFromApiResponse(result, total))
    }
  }
}

export function* watchSerialClickFromProdSchedule() {
  while (true) {
    const { id } = yield take(SET_SERIAL_NUMBER_SELECT)
    const { productionSchedule } = yield select(getDashboardSlice)
    const clickedVehicle = productionSchedule.filter((v) => v.id === id)
    const {
      dashboardType,
      dataBookCodes,
      isLeadSerial,
      leadSerialNumber,
      isPreProduction,
      isAftermarket,
    } = clickedVehicle[0]

    yield put({
      type: dashActions.UPDATE_DASH_VIEW,
      serial: id,
      dashType: dashboardType,
      dataBookCodes,
      isLeadSerial,
      leadSerialNumber,
      isPreProduction,
      isAftermarket,
    })
  }
}

export function* watchSaveDash() {
  while (true) {
    const action = yield take(SAVE_DASH)
    const { notes } = action

    const [
      components,
      removed,
      unassigned,
      serialNumber,
      unassignedComponents,
      updatedLocations,
      selectedCustomGraphics,
      alliumModelId,
      isAllium,
      isPreProduction,
    ] = yield all([
      select(getDashComponentsList),
      select(getRemovedComponentsList),
      select(getDashUnassignedList),
      select(getDashVehicleSerialNumber),
      select(getUnassignedComponents),
      select(getUpdatedLocations),
      select(getSelectedCustomGraphics),
      select(getAlliumModelId),
      select(getIsAllium),
      select(getDashIsPreProduction),
    ])

    if (length(updatedLocations)) {
      yield all(
        updatedLocations.map((location) =>
          put(
            saveLayoutLocation({
              id: location.id,
              body: {
                notes,
                layout: location,
              },
            })
          )
        )
      )
    }

    if (length(selectedCustomGraphics)) {
      yield put(saveCustomSwitchGraphics(notes))
    }

    const componentsList = createSaveComponentsList(
      components,
      removed,
      unassigned
    )

    // Detect swap save — isSwapReplacement flag set by reducer for same-signal swaps (Doc 13 - PBI 147363)
    const isSwapSave = components.some((c) => c.isSwapReplacement)
    const replacedSwitches = isSwapSave
      ? buildReplacedSwitchesList(components, removed)
      : []
    const componentsWithOutLocation = values(unassignedComponents)
    const requestBody = {
      alliumModelId,
      components: componentsList,
      isAllium,
      isPreProduction,
      notes,
    }

    yield put(saveDashConfig({ serialNumber, requestBody }))

    // Fork notification watcher — swap saves and pre-production saves show a persistent
    // closable banner (SNG_SAVE_SUCCESS); production non-swap saves show a transient toast
    // (Doc 13 - PBI 147363, Ticket 234884)
    yield fork(function* watchSaveResult() {
      const result = yield take([
        SUCCESS_SAVE_DASH_CONFIG,
        FAILURE_SAVE_DASH_CONFIG,
      ])
      if (result.type === SUCCESS_SAVE_DASH_CONFIG) {
        if (isSwapSave && replacedSwitches.length > 0) {
          try {
            yield call(postReplacedSwitches, {
              serialNumber,
              body: {
                replacedSwitches,
                notes,
                addedFeatures: [],
                removedFeatures: [],
              },
            })
          } catch (error) {
            yield put(
              createSystemNotification({
                notificationMessage:
                  'Failed to update features for replaced switches',
                type: 'error',
              })
            )
          }
        }
        yield put(requestVehicleFeatures(serialNumber))
        yield put(
          createSystemNotification(
            isSwapSave || isPreProduction
              ? {
                  notificationMessage: strings.switch_and_gauge_update_complete,
                  notificationId: SNG_SAVE_SUCCESS,
                  persistent: true,
                  closable: true,
                }
              : {
                  notificationMessage: strings.switch_and_gauge_update_complete,
                }
          )
        )
      }
    })
    if (componentsWithOutLocation.length === 0) {
      yield put(removePartsWithoutLocationBanner())
      yield put(removeSngPendingNotification())
    }
  }
}

export function* watchAllDashConfigsRecieved() {
  while (true) {
    const [dashboardAction, blacklistsAction] = yield all([
      take([SUCCESS_DASH_CONFIG, SUCCESS_SAVE_DASH_CONFIG]),
      take(SUCCESS_LAYOUT_BLACKLIST),
    ])
    const components = pathOr([], ['data', 'components'], dashboardAction)
    const { data = [] } = blacklistsAction
    const blacklists = data.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.location]: curr,
      }),
      {}
    )
    const blockedList = createBlockedList(components, blacklists)
    const hasComponentWithNoLocation = components.some(
      (comp) =>
        !prop('location', comp) &&
        !prop('isSwitchRemoved', comp) &&
        !prop('removedByEdex', comp)
    )
    if (hasComponentWithNoLocation) {
      yield put(displayPartsWithoutLocation())
    }
    yield put(setDashBlockedList(blockedList))
  }
}

/**
 * Watch dashboard get success action, and if it was
 * Allium data, display the Allium data banner notification
 */
export function* watchDashboardIsAllium() {
  while (true) {
    const {
      data: { isAllium },
    } = yield take(SUCCESS_DASH_CONFIG)

    if (isAllium) {
      yield put(displayAlliumDataBanner())
    }
  }
}

/**
 * Watch the dash save, if saved to allium
 * and allium returned errors, show the banner notification
 *
 * Disabled for now, as it seems to always return errors.
 */
// export function* watchAlliumSaveDashConfig() {
//   while (true) {
//     const {
//       data: {
//         dashboard: { isAllium },
//         alliumErrors,
//       },
//     } = yield take(SUCCESS_SAVE_DASH_CONFIG)

//     if (isAllium && Array.isArray(alliumErrors) && alliumErrors.length > 0) {
//       yield put(displaySavedWithAlliumErrorsBanner())
//     }
//   }
// }

export function* watchDashConfigRecievedSmartSwitches() {
  while (true) {
    const { data } = yield take([SUCCESS_DASH_CONFIG, SUCCESS_SAVE_DASH_CONFIG])
    if (truckThatCanAddSmartSwitches.includes(prop('vehicleSubType', data))) {
      let { vehicleSubType } = data
      const { isAllium = false, dashboardType, dataBookCodes } = data
      vehicleSubType = isDashTypeBus(
        vehicleSubType,
        dashboardType,
        dataBookCodes
      )
        ? TRUCK_SUBTYPE_BUS
        : vehicleSubType
      yield put(fetchVehicleSwitches({ vehicleSubType, isAllium }))
    }
  }
}

const createUpdatedComponentsList = (components, updatedParts) =>
  updatedParts.map((part) => {
    const componentFound = components.find(
      (component) => component.panelNumber === part.panelNumber
    )
    return { ...componentFound, capPartNumber: part.partNumber }
  })

export function* watchSaveCustomSwitchGraphics() {
  while (true) {
    const { notes } = yield take(SAVE_CUSTOM_SWITCH_GRAPHICS)

    const [
      components,
      updated,
      removedGraphics,
      serialNumber,
      unassignedComponents,
    ] = yield all([
      select(getDashComponentsList),
      select(getSelectedCustomGraphics),
      select(getRemovedCustomGraphics),
      select(getDashVehicleSerialNumber),
      select(getUnassignedComponents),
    ])

    const componentsWithOutLocation = values(unassignedComponents)

    const componentsList = createUpdatedComponentsList(
      [...components, ...componentsWithOutLocation],
      updated
    )

    const requestBody = {
      notes,
      components: componentsList,
      removedComponents: removedGraphics,
    }
    yield put(saveCustomSwitchConfig({ serialNumber, requestBody }))
    yield put(resetSwitchDetailsChanged())
  }
}
